import type { ReactNode } from 'react';
import { useContext } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { ReadOnlyField } from '@/lib/pages/forms/components/ReadOnlyField';

import { getMappedOptions } from '../../helpers';

const CustomSelect = ({
  control,
  options,
  name,
  label,
  placeholder,
  disabled,
  orientation = 'vertical',
}: {
  // TODO: Fix any type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  options: string[];
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
}) => {
  // console.log(placeholder);
  const mappedOptions = getMappedOptions(options);
  // console.log(mappedOptions, options);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={`flex ${orientation === 'horizontal' ? 'items-center justify-between' : 'w-full flex-col'}`}
        >
          <FormLabel
            className={`${orientation === 'horizontal' ? 'w-1/3' : 'mb-2'} font-semibold leading-[1.5rem]`}
          >
            {label}
          </FormLabel>
          <div
            className={`${orientation === 'horizontal' ? 'w-1/3' : ''} !mt-0`}
          >
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              value={field.value}
              disabled={disabled}
            >
              <FormControl tabIndex={0}>
                <SelectTrigger tabIndex={0}>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {mappedOptions.map(({ value, label: optionLabel }) => (
                  <SelectItem key={value} value={value}>
                    {optionLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
};

// TODO: Figure out where it was used as Select and rename export/import
export { CustomSelect as Selects };

export type Selectable<T extends string> = {
  value: T;
  label: string;
};

/**
 * Added onChange prop to allow for custom manual value changes.
 * the manual onChange will intercept the value change and call the onChange prop if provided.
 * if the onChange prop returns true, the control onChange from the form will be called.
 */
export const AlternativeSelect = <
  T extends FieldValues,
  U extends string,
  TTransformedValues extends FieldValues | undefined = T,
>({
  control,
  options,
  name,
  label,
  placeholder,
  disabled,
  orientation = 'vertical',
  required,
  stretch = false,
  wideLabel,
  readOnly,
  overwriteLabelWidth,
  overwriteInputWidth,
  labelClassName,
  nativeRequired = false,
  emptyValue = { value: null, label: '' },
  shouldShowNoneItem = true,
  onChange,
}: {
  control: Control<T, unknown, TTransformedValues>;
  options: Array<Selectable<U>>;
  name: Path<T> | string;
  label?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean | undefined;
  stretch?: boolean;
  wideLabel?: boolean;
  readOnly?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  labelClassName?: string;
  nativeRequired?: boolean;
  emptyValue?: { value: U | null; label: string };
  shouldShowNoneItem?: boolean;
  onChange?: (value: U | null) => boolean;
}) => {
  const { responsive, editMode } = useContext(AgentAuthorityContext);

  return (
    <FormField
      control={control}
      name={name as Path<T>}
      render={({ field }) => {
        if (!editMode || readOnly) {
          const optionLabel =
            (options || []).find(
              (option) => String(option.value) === String(field.value)
            )?.label || '';
          return (
            <ReadOnlyField
              label={label}
              value={optionLabel}
              orientation={orientation}
              stretch={stretch}
              wideLabel={wideLabel}
              responsive={responsive}
              overwriteInputWidth={overwriteInputWidth}
              overwriteLabelWidth={overwriteLabelWidth}
            />
          );
        } else {
          return (
            <FormItem
              className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'} ${responsive ? 'max-md:w-full max-md:flex-col' : ''}`}
            >
              {label ? (
                <FormLabel
                  className={`${orientation === 'horizontal' ? `${overwriteLabelWidth ? overwriteLabelWidth : `${stretch ? `${wideLabel ? 'w-2/3' : 'w-1/3'}` : `${wideLabel ? 'w-2/5' : 'w-1/5'}`}`}` : 'mb-2'} font-semibold leading-[1.5rem] ${responsive ? 'max-md:w-full' : ''} ${labelClassName}`}
                >
                  {label}
                  {(required || nativeRequired) && !disabled ? (
                    <span className="text-red-400">*</span>
                  ) : null}
                </FormLabel>
              ) : null}
              <div
                className={`${orientation === 'horizontal' ? `${overwriteInputWidth ? overwriteInputWidth : `${stretch ? `${wideLabel ? 'w-1/3' : 'w-2/3'}` : `${wideLabel ? 'w-1/5' : 'w-2/5'}`}`}` : ''} ${responsive ? 'max-md:w-full' : ''} !mt-0`}
              >
                <div className="relative">
                  <Select
                    onValueChange={(value) => {
                      if (onChange) {
                        if (onChange(value as U)) {
                          field.onChange(value as U);
                        }
                      } else {
                        field.onChange(value as U);
                      }
                    }}
                    // Radix matches the selected item with ===, so a numeric
                    // RHF value never matches a string option value.
                    value={field.value == null ? '' : String(field.value)}
                    disabled={disabled}
                    required={nativeRequired}
                    name={name as string}
                    // required={required} // uncomment to enable FE validation
                  >
                    <FormControl className="items-start overflow-clip px-[10px] [&>span]:text-start">
                      <SelectTrigger tabIndex={0}>
                        <SelectValue placeholder={placeholder} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {required
                        ? options.map(({ value, label }, idx) => (
                            <SelectItem
                              key={`${name}_${value}_${idx}`}
                              value={value}
                              className="min-h-6"
                            >
                              {label}
                            </SelectItem>
                          ))
                        : [
                            ...(shouldShowNoneItem ? [emptyValue] : []),
                            ...options,
                          ].map(({ value, label }, idx) => (
                            <SelectItem
                              key={`${name}_${value}_${idx}`}
                              value={value as string}
                              className="min-h-6"
                            >
                              {label}
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>
                <FormMessage />
              </div>
            </FormItem>
          );
        }
      }}
    />
  );
};

export { AlternativeSelect as Select };
